import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { UntypedFormControl, FormsModule, ReactiveFormsModule, ValidationErrors } from '@angular/forms';


import { ErrorMessageSpanComponent } from './components/error-message-span/error-message-span.component';
import { UnknownErrorMessagesComponent } from './components/unknown-error-messages/unknown-error-messages.component';
import { CartBadgeContainerComponent } from './container/cart-badge-container/cart-badge-container.component';
import { CartBadgeComponent } from './components/cart-badge/cart-badge.component';
import { ReplaceString } from './pipes/replace-string.pipe';
import { FriendlyAddressPipe } from './pipes/friendly-address.pipe';
import { QuantityInputComponent } from './components/quantity-input/quantity-input.component';
import { LoaderComponent } from './loader/loader.component';
import { CollectionPanelBoxComponent } from './collection-panel-box/collection-panel-box.component';
import { AccordionHeaderEmptyComponent } from './accordion-header-empty/accordion-header-empty.component';
import { DisplayPanelBoxComponent } from './components/display-panel-box/display-panel-box.component';
import { MultilineAddressPipe } from './pipes/multiline-address.pipe';
import { DisplayPanelBodyLineComponent } from './components/display-panel-body-line/display-panel-body-line.component';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { KeepHtmlPipe } from './pipes/keep-html.pipe';
import { GroupIconComponent } from './components/group-icon/group-icon.component';
import { CartItemIconComponent } from './components/cart-item-icon/cart-item-icon.component';
import { AnalyticalTestDurationPipe } from './pipes/analytical-test-duration.pipe';
import { HillsDatePipe } from './pipes/hills-date.pipe';
import { LineProgressBarComponent } from './components/line-progress-bar/line-progress-bar.component';
import { UiProgressIndicatorComponent } from '../utils/ui-progress-indicator/ui-progress-indicator.component';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzRateModule } from 'ng-zorro-antd/rate';
import { NzResultModule } from 'ng-zorro-antd/result';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzStatisticModule } from 'ng-zorro-antd/statistic';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { SidebarCartItemComponent } from '../cart/sidebar-cart/components/sidebar-cart-item/sidebar-cart-item.component';
import { CartItemPriceComponent } from './components/cart-item-price/cart-item-price.component';
import { SearchInputComponent } from './components/search-input/search-input.component';
import { EmptyDataComponent } from './components/empty-data/empty-data.component';
import { GeneralErrorComponent } from '@app/shared/components/error-pages/general-error/general-error.component';
import { NotFoundComponent } from '@app/shared/components/error-pages/not-found/not-found.component';
import { ServiceRequestErrorComponent } from '@app/shared/components/error-pages/service-request-error/service-request-error.component';
import { RouterModule } from '@angular/router';
import { DebounceClickDirective } from './directives/debounce-click.directive';
import { GridModule } from '@progress/kendo-angular-grid';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { LabelModule } from '@progress/kendo-angular-label';
import { RequestedItemFilterPipe } from './pipes/requested-item-filter.pipe';
import { CheckBoxModule } from '@progress/kendo-angular-inputs';
import { KebabCasePipe } from './pipes/kebab-case.pipe';
import { MyLabConfigService } from './mylab-config/mylab-config.service';
import { CommonClient } from '@app/clients/client.generated';
import { LoginComponent } from './login/login.component';
import { MatFormField, MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';
import { MaskitoDirective } from '@maskito/angular';
import { LoginModalComponent } from './login/login-modal/login-modal.component';
import { LocalDatePipe } from '@app/shared/pipes/local-date.pipe';

@NgModule({
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        CommonModule,
        ReactiveFormsModule,
        // ant-d
        NzGridModule,
        NzCollapseModule,
        NzListModule,
        NzSpinModule,
        NzIconModule,
        NzButtonModule,
        NzCardModule,
        NzMenuModule,
        NzSelectModule,
        NzFormModule,
        NzInputModule,
        NzAutocompleteModule,
        NzAvatarModule,
        NzTableModule,
        NzTypographyModule,
        NzTagModule,
        NzRateModule,
        NzCheckboxModule,
        NzStepsModule,
        NzSwitchModule,
        NzAlertModule,
        NzModalModule,
        NzPopoverModule,
        NzBadgeModule,
        NzToolTipModule,
        NzStatisticModule,
        NzSkeletonModule,
        NzResultModule,
        NzEmptyModule,
        NzInputNumberModule,
        NzDatePickerModule,
        NzDividerModule,
        RouterModule,
        GridModule,
        DropDownsModule,
        LabelModule,
        CheckBoxModule,
        MatInputModule,
        MatFormField,
        MatAutocompleteModule,
        MatSelectModule,
        MaskitoDirective
    ],
    declarations: [
        ErrorMessageSpanComponent,
        UnknownErrorMessagesComponent,
        CartBadgeContainerComponent,
        CartBadgeComponent,
        QuantityInputComponent,
        FriendlyAddressPipe,
        LoaderComponent,
        CollectionPanelBoxComponent,
        AccordionHeaderEmptyComponent,
        ReplaceString,
        KebabCasePipe,
        DisplayPanelBoxComponent,
        DisplayPanelBodyLineComponent,
        MultilineAddressPipe,
        KeepHtmlPipe,
        GroupIconComponent,
        AnalyticalTestDurationPipe,
        HillsDatePipe,
        LineProgressBarComponent,
        UiProgressIndicatorComponent,
        CartItemIconComponent,
        SidebarCartItemComponent,
        CartItemPriceComponent,
        SearchInputComponent,
        EmptyDataComponent,
        GeneralErrorComponent,
        NotFoundComponent,
        ServiceRequestErrorComponent,
        EmptyDataComponent,
        DebounceClickDirective,
        RequestedItemFilterPipe,
        LoginComponent,
        LoginModalComponent,
        LocalDatePipe
    ],
    exports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        ErrorMessageSpanComponent,
        UnknownErrorMessagesComponent,
        DisplayPanelBoxComponent,
        DisplayPanelBodyLineComponent,
        ReactiveFormsModule,
        LoaderComponent,
        CollectionPanelBoxComponent,
        AccordionHeaderEmptyComponent,
        FormlyModule,
        CartBadgeContainerComponent,
        CartBadgeComponent,
        QuantityInputComponent,
        FriendlyAddressPipe,
        MultilineAddressPipe,
        KeepHtmlPipe,
        HillsDatePipe,
        RequestedItemFilterPipe,
        ReplaceString,
        KebabCasePipe,
        GroupIconComponent,
        LineProgressBarComponent,
        UiProgressIndicatorComponent,
        SearchInputComponent,
        EmptyDataComponent,
        CartItemIconComponent,
        SidebarCartItemComponent,
        CartItemPriceComponent,
        GeneralErrorComponent,
        NotFoundComponent,
        ServiceRequestErrorComponent,
        AnalyticalTestDurationPipe,
        DebounceClickDirective,

        // ant-d
        NzGridModule,
        NzCollapseModule,
        NzListModule,
        NzSpinModule,
        NzIconModule,
        NzButtonModule,
        NzCardModule,
        NzMenuModule,
        NzSelectModule,
        NzAlertModule,
        NzAvatarModule,
        NzFormModule,
        NzInputModule,
        NzAutocompleteModule,
        NzDividerModule,
        NzEmptyModule,
        NzPopconfirmModule,
        NzTableModule,
        NzTypographyModule,
        NzTagModule,
        NzRateModule,
        NzCheckboxModule,
        NzStepsModule,
        NzSwitchModule,
        NzModalModule,
        NzPopoverModule,
        NzBadgeModule,
        NzToolTipModule,
        NzStatisticModule,
        NzSkeletonModule,
        NzResultModule,
        NzInputNumberModule,
        NzDatePickerModule,
        GridModule,
        DropDownsModule,
        LabelModule,
        CheckBoxModule,
        LoginComponent,
        LoginModalComponent,
        LocalDatePipe
    ],
    providers: [
        CommonClient
    ]
})
export class SharedModule {
}

export function RequiredFieldValidationMessage(err, field: FormlyFieldConfig) {
    return ` ${field.templateOptions.label} is required`;
}

export function NonEmptyFieldValidator(control: UntypedFormControl): ValidationErrors {
    return /[^$|\s]/.test(control.value) ? null : { 'required': true };
}
